/**
 * Pure rules for the contribution screens (admin and member). No React, no network.
 * Money here always means successful payments, the same rule the backend uses everywhere.
 */
import moment from "moment";
import { formatNaira } from "../../utils/dashboardFormat";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const KINDS = ["savings", "shares", "other"];

export const KIND_META = {
  savings: { label: "Savings", tone: "green" },
  shares: { label: "Shares", tone: "purple" },
  other: { label: "Other", tone: "blue" },
};

/** The kind of a category row, falling back to the old withdrawable flag when the kind was never set. */
export function kindOf(row) {
  const k = String(row?.category_kind || "").toLowerCase().trim();
  if (KINDS.includes(k)) return k;
  return Number(row?.is_withdrawable) === 1 ? "savings" : "other";
}

export const STATUS_META = {
  paid_up: { label: "Paid up", tone: "green" },
  part_paid: { label: "Part paid", tone: "amber" },
  not_started: { label: "Not started", tone: "grey" },
  contributed: { label: "Contributed", tone: "blue" },
};

/** Payment status as shown on a payment row. */
export function paymentStatusMeta(status) {
  const s = String(status || "").toUpperCase();
  if (s === "SUCCESSFUL" || s === "SUCCESS" || s === "PAID") return { label: "Successful", tone: "green" };
  if (s === "PENDING" || s === "PROCESSING") return { label: "Pending", tone: "amber" };
  if (s === "FAILED" || s === "DECLINED" || s === "CANCELED" || s === "CANCELLED") return { label: s.charAt(0) + s.slice(1).toLowerCase(), tone: "red" };
  return { label: s ? s.charAt(0) + s.slice(1).toLowerCase() : "Unknown", tone: "grey" };
}

/** "20 May 2026" or "" (never "Invalid date"). */
export function whenLabel(value, withTime = false) {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid()) return "";
  return m.format(withTime ? "DD MMM YYYY, HH:mm" : "DD MMM YYYY");
}

/** Normalise one category row from the overview endpoint. */
export function normalizeCategory(row) {
  const r = row || {};
  return {
    id: r.id,
    name: r.name || "Unnamed",
    kind: kindOf(r),
    system: Boolean(r.is_system),
    collected: num(r.collected),
    thisMonth: num(r.this_month),
    paymentsCount: num(r.payments_count),
    contributors: num(r.contributors),
    paidUp: num(r.paid_up_count),
    partPaid: num(r.part_paid_count),
    perMember: r.amount_per_member != null ? num(r.amount_per_member) : null,
    target: r.target_amount != null ? num(r.target_amount) : null,
    progress: r.progress_percent != null ? num(r.progress_percent) : null,
    lastPaymentAt: r.last_payment_at || null,
    startDate: r.start_date || null,
    endDate: r.end_date || null,
  };
}

export function normalizeOverview(data) {
  const d = data || {};
  const t = d.totals || {};
  return {
    totals: {
      collected: num(t.collected),
      thisMonth: num(t.this_month),
      contributors: num(t.contributors),
      membersTotal: num(t.members_total),
      categories: num(t.categories_count),
    },
    categories: (Array.isArray(d.categories) ? d.categories : []).map(normalizeCategory),
    approximate: Boolean(d.approximate),
  };
}

/**
 * The old list endpoint only knows names and a total per category. Used when the new overview endpoint is not
 * there yet, so the page still works (with fewer details) instead of breaking.
 */
export function overviewFromLegacy(legacy) {
  const rows = Array.isArray(legacy?.data?.categories) ? legacy.data.categories : Array.isArray(legacy?.categories) ? legacy.categories : [];
  const categories = rows.map((r) =>
    normalizeCategory({
      id: r.id,
      name: r.name,
      category_kind: r.category_kind,
      is_withdrawable: r.is_withdrawable,
      is_system: r.is_system,
      collected: r.amount_contributed,
      amount_per_member: num(r.amount_per_individuals) > 0 ? r.amount_per_individuals : null,
      target_amount: num(r.target_amount) > 0 ? r.target_amount : null,
      progress_percent: num(r.target_amount) > 0 ? Math.min(100, Math.round((num(r.amount_contributed) / num(r.target_amount)) * 100)) : null,
      start_date: r.start_date,
      end_date: r.end_date,
    })
  );
  return {
    totals: { collected: categories.reduce((s, c) => s + c.collected, 0), thisMonth: 0, contributors: 0, membersTotal: 0, categories: categories.length },
    categories,
    approximate: true,
  };
}

export const kindTabs = (categories) => [
  { key: "all", label: "All", count: categories.length },
  ...KINDS.map((k) => ({ key: k, label: KIND_META[k].label, count: categories.filter((c) => c.kind === k).length })),
];

export const validKind = (value) => (KINDS.includes(String(value || "").toLowerCase()) ? String(value).toLowerCase() : "all");

/** The line under a category's name in the overview: how far along it is, in the words that fit. */
export function progressText(c) {
  if (c.target) return `${formatNaira(c.collected)} of ${formatNaira(c.target)} target`;
  if (c.perMember) return c.paidUp || c.partPaid ? `${c.paidUp} paid up, ${c.partPaid} part paid` : `${formatNaira(c.perMember)} per member`;
  return "No target";
}

/** How many members have contributed, when the denominator is known. */
export function contributorsText(c, membersTotal) {
  if (!c.contributors) return "No contributors yet";
  if (membersTotal > 0) return `${c.contributors} of ${membersTotal} members`;
  return `${c.contributors} ${c.contributors === 1 ? "contributor" : "contributors"}`;
}

/** Share of members that contributed, 0-100, or null when we cannot say. */
export function contributorShare(c, membersTotal) {
  if (!membersTotal) return null;
  return Math.min(100, Math.round((c.contributors / membersTotal) * 100));
}

export function normalizeContributors(data) {
  const d = data || {};
  const counts = d.counts || {};
  const p = d.pagination || {};
  return {
    category: d.category ? normalizeCategory(d.category) : null,
    counts: {
      paidUp: num(counts.paid_up),
      partPaid: num(counts.part_paid),
      notStarted: num(counts.not_started),
      hasPaid: num(counts.has_paid),
    },
    members: (Array.isArray(d.members) ? d.members : []).map((m) => ({
      id: m.member_id,
      name: m.name || "Member",
      memberNo: m.member_no || "",
      photo: m.photo || "",
      paid: num(m.paid),
      remaining: m.remaining != null ? num(m.remaining) : null,
      payments: num(m.payments_count),
      lastPaymentAt: m.last_payment_at || null,
      status: m.status || "contributed",
    })),
    pagination: { total: num(p.total), page: num(p.currentPage) || 1, pages: num(p.totalPages) || 1, perPage: num(p.perPage) || 20 },
  };
}

/** Status filter chips for the contributors tab; per-member statuses only make sense with a per-member amount. */
export function contributorFilters(category, counts) {
  const base = [{ key: "has_paid", label: "Has paid", count: counts.hasPaid }];
  if (category?.perMember) {
    base.push({ key: "paid_up", label: "Paid up", count: counts.paidUp }, { key: "part_paid", label: "Part paid", count: counts.partPaid });
  }
  base.push({ key: "not_started", label: "Not started", count: counts.notStarted }, { key: "all", label: "Everyone", count: counts.hasPaid + counts.notStarted });
  return base;
}

/** Rows of the payments list (admin all-payments / category payments). */
export function normalizePayments(data) {
  const d = data || {};
  const p = d.pagination || {};
  const s = d.summary || {};
  return {
    rows: Array.isArray(d.contributions) ? d.contributions : Array.isArray(d.histories) ? d.histories : [],
    pagination: { total: num(p.total), page: num(p.currentPage) || 1, pages: num(p.totalPages) || 1, perPage: num(p.perPage) || 20 },
    summary: { count: num(s.payments_count), total: num(s.total_amount), pending: num(s.pending_count), known: data != null && d.summary != null },
  };
}

/** The member's own categories, split into what to show and what is waiting. */
export function normalizeMemberCategories(data) {
  const d = data || {};
  const t = d.totals || {};
  const categories = (Array.isArray(d.categories) ? d.categories : []).map((c) => ({
    id: c.id,
    name: c.name || "Unnamed",
    kind: kindOf(c),
    paid: num(c.paid),
    pending: num(c.pending),
    pendingCount: num(c.pending_count),
    payments: num(c.payments_count),
    lastPaymentAt: c.last_payment_at || null,
    perMember: c.amount_per_member != null ? num(c.amount_per_member) : null,
    remaining: c.remaining != null ? num(c.remaining) : null,
    progress: c.progress_percent != null ? num(c.progress_percent) : null,
    status: c.status || "contributed",
    endDate: c.end_date || null,
  }));
  return {
    totals: {
      total: num(t.total_contribution ?? t.total_contributed),
      savings: num(t.total_savings),
      shares: num(t.total_shares),
    },
    // Categories with money or a per-member amount to meet come first; empty optional ones go last.
    categories: [...categories].sort((a, b) => {
      const rank = (c) => (c.paid > 0 || c.pending > 0 ? 0 : c.perMember ? 1 : 2);
      return rank(a) - rank(b) || b.paid - a.paid || a.name.localeCompare(b.name);
    }),
    pendingTotal: categories.reduce((sum, c) => sum + c.pending, 0),
    pendingCount: categories.reduce((sum, c) => sum + c.pendingCount, 0),
  };
}

/** One payment row from the history endpoint, flattened for display. */
export function normalizePaymentRow(r) {
  const row = r || {};
  const member = row.member || {};
  const name = [member.first_name, member.last_name, member.other_name].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const category = row.category || row.contribution_category || {};
  const recorder = row.recordedBy || row.recorded_by || null;
  return {
    id: row.contributionId ?? row.id,
    date: row.payment_date || row.paymentDate || row.created_at || null,
    createdAt: row.created_at || null,
    memberId: row.memberId ?? member.id ?? null,
    memberName: name || "Member",
    memberNo: member.member_no || "",
    photo: member.photo || "",
    categoryId: row.categoryId ?? category.id ?? null,
    categoryName: category.name || "",
    kind: kindOf(category),
    amount: num(row.amount),
    method: String(row.payment_method || row.payment_type?.name || "").replace(/_/g, " "),
    code: row.transactionCode || row.transaction_code || "",
    status: paymentStatusMeta(row.status),
    recordedBy: recorder?.name || "",
  };
}

/** Quick date ranges for filters. `today` is injectable so tests do not depend on the clock. */
export function dateRange(key, today = new Date()) {
  const fmt = (d) => moment(d).format("YYYY-MM-DD");
  const t = moment(today);
  if (key === "today") return { from: fmt(t), to: fmt(t) };
  if (key === "week") return { from: fmt(t.clone().startOf("isoWeek")), to: fmt(t) };
  if (key === "month") return { from: fmt(t.clone().startOf("month")), to: fmt(t) };
  return { from: "", to: "" };
}

/** The selectable lists come back in a few shapes; take whichever array is there. */
export function listFrom(data, ...keys) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  if (Array.isArray(data.data)) return data.data;
  if (data.data && typeof data.data === "object") {
    for (const key of keys) {
      if (Array.isArray(data.data[key])) return data.data[key];
    }
  }
  return [];
}

/** How many filters are on, and a readable name for each, so people can see and clear them. */
export function activeFilters({ member, category, method, range, search }, names = {}) {
  const list = [];
  if (search) list.push({ key: "search", label: `"${search}"` });
  if (member) list.push({ key: "member", label: names.member || "One member" });
  if (category) list.push({ key: "category", label: names.category || "One category" });
  if (method) list.push({ key: "method", label: names.method || "One method" });
  if (range && range !== "all") list.push({ key: "range", label: { today: "Today", week: "This week", month: "This month" }[range] || "Custom dates" });
  return list;
}
